<script>
import { logout as logoutApi, fetchMe } from '@/api/auth'
import {
  clearAuthStorage,
  getRefreshToken,
  hasAccessToken
} from '@/utils/auth-storage'
export default {
  name: 'AppSideMenu',
  props: {
    open: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const cachedName = localStorage.getItem('userName') || sessionStorage.getItem('userName')
    return {
      isLoggedIn: false,
      authProfile: cachedName ? { name: cachedName } : null
    }
  },
  computed: {
    displayName() {
      const name = this.authProfile?.name
      return typeof name === 'string' && name.trim() ? name.trim() : '회원'
    },
    needsIdentityVerification() {
      if (!this.isLoggedIn || !this.authProfile) return false
      if (typeof this.authProfile.verified === 'boolean') {
        return this.authProfile.verified !== true
      }
      const authYn = this.authProfile.authYn
      const authDt = this.authProfile.authDt
      const authCid = this.authProfile.authCid
      const hasCid = typeof authCid === 'string' && authCid.trim().length > 0
      return !(String(authYn || '').toUpperCase() === 'Y' && authDt && hasCid)
    },
    /** 본인인증 필요 시 문구는 템플릿에서 2줄(이름 truncate / 안내)로 표시 */
    userTitleText() {
      if (!this.isLoggedIn) return '로그인을 해주세요.'
      return `${this.displayName}님`
    },
    sectionGroups() {
      return [
        {
          title: '서비스',
          items: [
            { label: '호가 주문창', path: '/home/gold', icon: 'chart' },
            { label: '감정 예상가 확인하기', path: '/appraisal/step1', icon: 'glasses' }
          ]
        },
        {
          title: '내역',
          items: [{ label: '내역', path: '/history', icon: 'file' }]
        },
        {
          title: '계좌',
          items: [
            { label: '입출금 계좌', path: '/account/step1', icon: 'bank' },
            { label: '입금 하기', path: '/deposit', icon: 'transferIn' },
            { label: '출금 하기', path: '/withdraw', icon: 'transferOut' }
          ]
        },
        {
          title: '안내 및 문의',
          items: [
            { label: '공지사항', path: '/notice', icon: 'notice' },
            { label: '1:1문의', path: '/inquiry', icon: 'chat' },
            { label: '이용약관', path: null, icon: 'book' }
          ]
        }
      ]
    },
    goldBorder15Style() {
      return {
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'rgba(201, 169, 98, 0.15)'
      }
    }
  },
  methods: {
    async syncAuthState() {
      this.isLoggedIn = hasAccessToken()
      if (!this.isLoggedIn) {
        this.authProfile = null
        return
      }

      try {
        const result = await fetchMe()
        if (result?.code === 0 && result?.data) {
          this.authProfile = result.data
          if (result.data.name) {
            localStorage.setItem('userName', result.data.name)
          }
        } else {
          this.authProfile = null
        }
      } catch (_error) {
        this.authProfile = null
      }
    },
    close() {
      this.$emit('close')
    },
    navigate(path) {
      if (!path) return
      this.close()
      if (this.$route.path !== path) {
        this.$router.push(path)
      }
    },
    goMyInfo() {
      this.navigate('/my-info')
    },
    goIdentityVerificationStart() {
      if (!this.isLoggedIn) return
      const redirect = this.$route?.fullPath || '/home'
      this.close()
      this.$router.push({
        path: '/auth/verify/start',
        query: { redirect }
      })
    },
    logout() {
      this.requestLogout()
    },
    async requestLogout() {
      const refreshToken = getRefreshToken()

      try {
        if (refreshToken) {
          await logoutApi({ refreshToken })
        }
      } catch (_error) {
        // 서버 무효화 실패여도 클라이언트는 즉시 로그아웃 처리한다.
      } finally {
        clearAuthStorage()
        this.close()
        this.$router.replace('/login')
      }
    },
    isItemActive(item) {
      if (!item.path) return false
      const p = this.$route.path
      const ip = item.path
      if (p === ip) return true
      if (ip.length > 1 && p.startsWith(`${ip}/`)) return true
      return false
    },
    itemButtonClass(item) {
      const active = this.isItemActive(item)
      const base =
        'w-full rounded-[7px] px-4 py-4 flex items-center justify-between min-h-touch transition-colors bg-[#191919]'
      if (active) {
        return `${base} bg-[#222120]`
      }
      return `${base} active:bg-[#222120]`
    },
  },
  mounted() {
    this.syncAuthState()
  },
  watch: {
    open(nextOpen) {
      if (nextOpen) {
        this.syncAuthState()
      }
    }
  }
}
</script>

<template>
  <transition name="side-menu">
    <div v-if="open" class="fixed inset-0 z-[70] flex justify-center bg-[#121212]">
      <aside class="w-full max-w-[430px] min-h-screen flex flex-col bg-[#121212]">
        <div class="flex-1 overflow-y-auto px-4 pt-safe-top pb-[calc(1rem+var(--safe-area-bottom,0px))]">
          <div class="pt-5">
            <div class="flex items-center justify-between">
              <div class="h-[26px] w-[26px] shrink-0" aria-hidden="true" />
              <p class="font-noto-kr font-medium text-[17px] leading-[24px] text-gold-500">전체메뉴</p>
              <button
                type="button"
                class="flex h-[26px] w-[26px] shrink-0 items-center justify-center min-h-touch text-gold-500"
                aria-label="닫기"
                @click="close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
            </div>
          </div>

          <div class="mt-4 flex items-center gap-2">
            <div class="flex min-w-0 flex-1 items-center gap-2">
              <div
                class="flex shrink-0 items-center justify-center rounded-full bg-gradient-gold"
                :class="needsIdentityVerification ? 'h-9 w-9' : 'h-[22px] w-[22px]'"
              >
                <svg
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="shrink-0"
                  :class="needsIdentityVerification ? 'h-[18px] w-[18px]' : 'h-[14px] w-[14px]'"
                  aria-hidden="true"
                >
                  <circle cx="7" cy="5" r="3" stroke="#F9F6EF" stroke-width="1.2"/>
                  <path d="M2 12c0-2.21 2.24-4 5-4s5 1.79 5 4" stroke="#F9F6EF" stroke-width="1.2"/>
                </svg>
              </div>
              <button
                v-if="needsIdentityVerification"
                type="button"
                class="flex min-w-0 flex-1 flex-col items-stretch gap-0.5 text-left text-gold-500 active:opacity-90"
                @click="goIdentityVerificationStart"
              >
                <span class="min-w-0 truncate font-noto-kr text-[13px] font-bold leading-[18px]">{{ displayName }}님</span>
                <span class="font-noto-kr text-[12px] font-semibold leading-[17px] text-gold-500/95">본인인증후 이용 가능</span>
              </button>
              <p v-else class="min-w-0 flex-1 truncate font-noto-kr font-bold text-[13px] leading-[18px] text-gold-500">{{ userTitleText }}</p>
            </div>
            <button
              type="button"
              class="flex shrink-0 flex-nowrap items-center gap-1 whitespace-nowrap rounded-full bg-[#715F3B] px-3 py-2 font-noto-kr text-[11px] leading-[15px] tracking-[0.5px] text-gold-50 transition-colors active:opacity-90"
              @click="goMyInfo"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="shrink-0" aria-hidden="true">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M12.25 4.08341L7 1.16675L1.75 4.08341V9.91675L7 12.8334L12.25 9.91675V4.08341ZM7 8.94453C8.11602 8.94453 9.02073 8.07397 9.02073 7.00008C9.02073 5.92619 8.11602 5.05564 7 5.05564C5.88398 5.05564 4.97927 5.92619 4.97927 7.00008C4.97927 8.07397 5.88398 8.94453 7 8.94453Z"
                  stroke="currentColor"
                  stroke-linejoin="round"
                />
              </svg>
              내 정보 변경
            </button>
          </div>

          <section
            v-for="group in sectionGroups"
            :key="group.title"
            class="mt-4 flex flex-col gap-4 rounded-[14px] bg-[#1C1B1A] p-4"
          >
            <p class="font-noto-kr font-medium text-[17px] leading-[24px] text-gold-500">{{ group.title }}</p>
            <button
              v-for="item in group.items"
              :key="`${group.title}-${item.label}`"
              type="button"
              :class="itemButtonClass(item)"
              :style="goldBorder15Style"
              @click="navigate(item.path)"
            >
              <div class="flex min-w-0 items-center gap-3">
                <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-[7px] bg-[#2E2D2B] text-white">
                  <!-- 1 호가 주문창 -->
                  <svg
                    v-if="item.icon === 'chart'"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="shrink-0"
                  >
                    <path
                      d="M5.28294 12.25V6.84733C5.28294 6.29505 5.73066 5.84733 6.28294 5.84733H7.81692C8.3692 5.84733 8.81692 6.29505 8.81692 6.84733V12.25M5.28294 12.25L5.28364 10.147C5.28383 9.59458 4.83606 9.14667 4.28364 9.14667H2.75C2.19772 9.14667 1.75 9.59438 1.75 10.1467V11.25C1.75 11.8023 2.19771 12.25 2.75 12.25H5.28294ZM5.28294 12.25H8.81692M8.81692 12.25V2.75C8.81692 2.19772 9.26463 1.75 9.81692 1.75H11.25C11.8023 1.75 12.25 2.19772 12.25 2.75V11.25C12.25 11.8023 11.8023 12.25 11.25 12.25H8.81692Z"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <!-- 2 감정 예상가 -->
                  <svg
                    v-else-if="item.icon === 'glasses'"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="shrink-0"
                  >
                    <path
                      d="M5.3199 7.00004H4.8199C4.8199 7.80638 4.16624 8.46004 3.3599 8.46004V8.96004V9.46004C4.71852 9.46004 5.8199 8.35866 5.8199 7.00004H5.3199ZM3.3599 8.96004V8.46004C2.55357 8.46004 1.8999 7.80638 1.8999 7.00004H1.3999H0.899902C0.899902 8.35866 2.00128 9.46004 3.3599 9.46004V8.96004ZM1.3999 7.00004H1.8999C1.8999 6.1937 2.55357 5.54004 3.3599 5.54004V5.04004V4.54004C2.00128 4.54004 0.899902 5.64142 0.899902 7.00004H1.3999ZM3.3599 5.04004V5.54004C4.16624 5.54004 4.8199 6.1937 4.8199 7.00004H5.3199H5.8199C5.8199 5.64142 4.71852 4.54004 3.3599 4.54004V5.04004ZM5.30213 6.58004L5.73493 6.8304C5.98813 6.3927 6.46014 6.10004 6.99991 6.10004V5.60004V5.10004C6.08874 5.10004 5.29392 5.59569 4.86933 6.32968L5.30213 6.58004ZM6.99991 5.60004V6.10004C7.53969 6.10004 8.0117 6.3927 8.2649 6.8304L8.6977 6.58004L9.1305 6.32968C8.70591 5.59569 7.91109 5.10004 6.99991 5.10004V5.60004ZM12.5999 7.00004H12.0999C12.0999 7.80638 11.4462 8.46004 10.6399 8.46004V8.96004V9.46004C11.9985 9.46004 13.0999 8.35866 13.0999 7.00004H12.5999ZM10.6399 8.96004V8.46004C9.83357 8.46004 9.1799 7.80638 9.1799 7.00004H8.6799H8.1799C8.1799 8.35866 9.28128 9.46004 10.6399 9.46004V8.96004ZM8.6799 7.00004H9.1799C9.1799 6.1937 9.83356 5.54004 10.6399 5.54004V5.04004V4.54004C9.28128 4.54004 8.1799 5.64142 8.1799 7.00004H8.6799ZM10.6399 5.04004V5.54004C11.4462 5.54004 12.0999 6.1937 12.0999 7.00004H12.5999H13.0999C13.0999 5.64142 11.9985 4.54004 10.6399 4.54004V5.04004Z"
                      fill="currentColor"
                    />
                  </svg>
                  <!-- 3 내역 -->
                  <svg
                    v-else-if="item.icon === 'file'"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="shrink-0"
                  >
                    <path
                      d="M4.89993 4.1999H9.09993M4.89993 6.2999H9.09993M4.89993 8.3999H6.99993M3.84976 1.3999H10.1499C10.9231 1.3999 11.5499 2.02672 11.5499 2.79993L11.5497 11.1999C11.5497 11.9731 10.9229 12.5999 10.1497 12.5999L3.8497 12.5999C3.0765 12.5999 2.4497 11.9731 2.44971 11.1999L2.44976 2.79989C2.44977 2.0267 3.07657 1.3999 3.84976 1.3999Z"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <!-- 4 입출금 계좌 -->
                  <svg
                    v-else-if="item.icon === 'bank'"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="shrink-0"
                  >
                    <path
                      d="M3.4999 5.6V8.05M10.4999 5.6V8.05M2.4499 10.5H11.5499C12.1298 10.5 12.5999 10.0299 12.5999 9.45V4.55C12.5999 3.9701 12.1298 3.5 11.5499 3.5H2.4499C1.87 3.5 1.3999 3.9701 1.3999 4.55V9.45C1.3999 10.0299 1.87 10.5 2.4499 10.5ZM8.3999 7C8.3999 7.7732 7.7731 8.4 6.9999 8.4C6.2267 8.4 5.5999 7.7732 5.5999 7C5.5999 6.2268 6.2267 5.6 6.9999 5.6C7.7731 5.6 8.3999 6.2268 8.3999 7Z"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <!-- 5 입금 -->
                  <svg
                    v-else-if="item.icon === 'transferIn'"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="shrink-0"
                  >
                    <path
                      d="M5.94981 4.54995C6.26812 3.14716 7.52264 2.09995 9.02175 2.09995C10.7614 2.09995 12.1717 3.51025 12.1717 5.24995C12.1717 6.86142 10.9617 8.19026 9.40064 8.3774M3.8498 3.49995L5.2498 2.09995L3.8498 0.699951M1.0498 4.89995V3.96662C1.0498 2.93569 1.88554 2.09995 2.91647 2.09995H4.31647M10.1498 10.5L8.74981 11.9L10.1498 13.3M12.9498 9.09995V10.0333C12.9498 11.0642 12.1141 11.9 11.0831 11.9H9.68314M7.34981 9.44995C7.34981 11.1896 5.9395 12.6 4.1998 12.6C2.46011 12.6 1.0498 11.1896 1.0498 9.44995C1.0498 7.71025 2.46011 6.29995 4.1998 6.29995C5.9395 6.29995 7.34981 7.71025 7.34981 9.44995Z"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <!-- 6 출금 -->
                  <svg
                    v-else-if="item.icon === 'transferOut'"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="shrink-0"
                  >
                    <path
                      d="M5.94981 4.54995C6.26812 3.14716 7.52264 2.09995 9.02175 2.09995C10.7614 2.09995 12.1717 3.51025 12.1717 5.24995C12.1717 6.86142 10.9617 8.19026 9.40064 8.3774M3.8498 3.49995L5.2498 2.09995L3.8498 0.699951M1.0498 4.89995V3.96662C1.0498 2.93569 1.88554 2.09995 2.91647 2.09995H4.31647M10.1498 10.5L8.74981 11.9L10.1498 13.3M12.9498 9.09995V10.0333C12.9498 11.0642 12.1141 11.9 11.0831 11.9H9.68314M7.34981 9.44995C7.34981 11.1896 5.9395 12.6 4.1998 12.6C2.46011 12.6 1.0498 11.1896 1.0498 9.44995C1.0498 7.71025 2.46011 6.29995 4.1998 6.29995C5.9395 6.29995 7.34981 7.71025 7.34981 9.44995Z"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <!-- 7 공지 -->
                  <svg
                    v-else-if="item.icon === 'notice'"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="shrink-0"
                  >
                    <path
                      d="M7 3.71875V5.6875M7 7.65625V7.6069M8.42663 9.51087L7 12.25L5.6875 9.51087H3.0625C2.33763 9.51087 1.75 8.92324 1.75 8.19837V3.0625C1.75 2.33763 2.33763 1.75 3.0625 1.75H10.9375C11.6624 1.75 12.25 2.33763 12.25 3.0625V8.19837C12.25 8.92324 11.6624 9.51087 10.9375 9.51087H8.42663Z"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <!-- 8 1:1문의 -->
                  <svg
                    v-else-if="item.icon === 'chat'"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="shrink-0"
                  >
                    <path
                      d="M4.1999 5.65266V5.60002M6.9999 5.65266V5.60002M9.7999 5.65266V5.60002M6.81729 9.67828L3.89555 12.6V9.67828H2.7999C2.0267 9.67828 1.3999 9.05148 1.3999 8.27828V2.80002C1.3999 2.02683 2.0267 1.40002 2.7999 1.40002H11.1999C11.9731 1.40002 12.5999 2.02683 12.5999 2.80002V8.27828C12.5999 9.05148 11.9731 9.67828 11.1999 9.67828H6.81729Z"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <!-- 9 이용약관 -->
                  <svg
                    v-else
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="shrink-0"
                  >
                    <path
                      d="M7.08106 11.9508C7.89265 11.3556 10.1327 10.5224 12.5999 11.9508V3.02323M1.3999 2.86091V11.9508C2.2115 11.3556 4.4515 10.5224 6.91874 11.9508V3.18555M1.3999 2.83801C2.2115 2.24284 4.4515 1.40961 6.91874 2.83801M7.08106 2.83801C7.89265 2.24284 10.1327 1.40961 12.5999 2.83801"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <span
                  class="truncate font-noto-kr text-[15px] leading-[22px]"
                  :class="isItemActive(item) ? 'text-[#F5F0E8]' : 'text-gold-100'"
                >
                  {{ item.label }}
                </span>
              </div>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                class="shrink-0 text-[#F0E9D5]"
                aria-hidden="true"
              >
                <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </section>

          <div class="mt-4">
            <button
              type="button"
              class="w-full min-h-touch rounded-[14px] bg-[#62605B] px-4 py-4 font-noto-kr text-[15px] leading-[22px] text-gold-100 transition-colors active:opacity-90"
              @click="logout"
            >
              로그아웃
            </button>
          </div>
        </div>
      </aside>
    </div>
  </transition>
</template>

<style scoped>
.side-menu-enter-active,
.side-menu-leave-active {
  transition: opacity 0.25s ease;
}

.side-menu-enter-from,
.side-menu-leave-to {
  opacity: 0;
}

.side-menu-enter-active aside,
.side-menu-leave-active aside {
  transition: transform 0.25s ease;
}

.side-menu-enter-from aside,
.side-menu-leave-to aside {
  transform: translateX(-100%);
}
</style>
